const DEFAULT_COUNTRY_CODE = "BG";
const DEFAULT_CURRENCY = "EUR";
const DEFAULT_MERCHANT_RETURN_DAYS = 14;
const OFFER_TYPES = ["Offer", "AggregateOffer"];

const getSchemaTypes = value => {
  const type = value?.["@type"];

  if (Array.isArray(type)) {
    return type;
  }

  return type ? [type] : [];
};

const isOfferType = value => getSchemaTypes(value).some(type => OFFER_TYPES.includes(type));

const isAggregateOffer = value => getSchemaTypes(value).includes("AggregateOffer");

const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const getValidityDates = date => {
  const year = date.getFullYear();

  return {
    validFrom: `${year}-01-01`,
    validThrough: `${year}-12-31`
  };
};

const isPastDate = (value, date) => {
  if (!value) {
    return false;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  const today = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  return parsedDate < today;
};

const getOfferCurrency = offer => offer.priceCurrency || offer.priceSpecification?.priceCurrency || DEFAULT_CURRENCY;

const getShippingDetails = ({ countryCode, currency }) => ({
  "@type": "OfferShippingDetails",
  shippingRate: {
    "@type": "MonetaryAmount",
    value: "0",
    currency
  },
  shippingDestination: {
    "@type": "DefinedRegion",
    addressCountry: countryCode
  },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 1,
      unitCode: "DAY"
    },
    transitTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 2,
      unitCode: "DAY"
    }
  }
});

const getMerchantReturnPolicy = ({ countryCode }) => ({
  "@type": "MerchantReturnPolicy",
  applicableCountry: countryCode,
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: DEFAULT_MERCHANT_RETURN_DAYS
});

const getAggregateOfferPriceRange = offer => {
  if (!isAggregateOffer(offer) || hasOwnProperty(offer, "highPrice") || !hasOwnProperty(offer, "lowPrice")) {
    return {};
  }

  return {
    highPrice: offer.lowPrice
  };
};

const enhanceOffer = (offer, options) => {
  const { countryCode, date } = options;
  const currency = getOfferCurrency(offer);
  const validityDates = getValidityDates(date);
  const priceValidUntil = isPastDate(offer.priceValidUntil, date) ? validityDates.validThrough : offer.priceValidUntil || validityDates.validThrough;

  return {
    ...offer,
    ...getAggregateOfferPriceRange(offer),
    priceValidUntil,
    validFrom: offer.validFrom || validityDates.validFrom,
    validThrough: offer.validThrough || priceValidUntil,
    shippingDetails: offer.shippingDetails || getShippingDetails({ countryCode, currency }),
    hasMerchantReturnPolicy: offer.hasMerchantReturnPolicy || getMerchantReturnPolicy({ countryCode })
  };
};

const enhanceNode = (value, options) => {
  if (Array.isArray(value)) {
    return value.map(item => enhanceNode(item, options));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const enhancedChildren = Object.entries(value).reduce(
    (acc, [key, childValue]) => ({
      ...acc,
      [key]: enhanceNode(childValue, options)
    }),
    {}
  );

  return isOfferType(enhancedChildren) ? enhanceOffer(enhancedChildren, options) : enhancedChildren;
};

export const enhanceMerchantListingSchema = (schemaMarkup, options = {}) => {
  if (!schemaMarkup) {
    return schemaMarkup;
  }

  return enhanceNode(schemaMarkup, {
    countryCode: options.countryCode || DEFAULT_COUNTRY_CODE,
    date: options.date || new Date()
  });
};
